import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import LandingLayout from '../layouts/LandingLayout';
import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardDescription, 
  CardContent 
} from '../components/ui-components/Card';
import Input from '../components/ui-components/Input';
import Button from '../components/ui-components/Button';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState<string | null>(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Check if user is returning from OAuth
  useEffect(() => {
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' && session) {
          toast.success('Logged in successfully!');
          navigate('/dashboard');
        }
      }
    );

    // Handle OAuth redirect
    const handleRedirect = async () => {
      const { error } = await supabase.auth.getSession();
      if (error) {
        toast.error('Authentication error. Please try again.');
      }
    };

    handleRedirect();

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    
    try {
      setIsLoading(true);
      await login(email, password);
      toast.success('Logged in successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to log in. Please check your credentials.');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOAuthLogin = async (provider: 'google' | 'microsoft') => {
    try {
      setOauthLoading(provider);
      const { error } = await supabase.auth.signInWithOAuth({
        provider: provider === 'google' ? 'google' : 'azure',
        options: {
          redirectTo: `${window.location.origin}/dashboard`,
        },
      });
      
      if (error) {
        toast.error(`Failed to log in with ${provider}. Please try again.`);
        console.error(error);
      }
    } catch (error) {
      toast.error(`An error occurred. Please try again.`);
      console.error(error);
    } finally {
      setOauthLoading(null);
    }
  };

  return (
    <LandingLayout>
      <div className="min-h-screen pt-24 pb-12 flex items-center justify-center px-6">
        <div className="absolute inset-0 bg-gradient-to-b from-accent/30 to-background -z-10"></div>
        
        <Card className="w-full max-w-md animate-slide-up glass">
          <CardHeader>
            <CardTitle>Welcome back</CardTitle>
            <CardDescription>Sign in to your Justbookit account</CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <Input
                label="Email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              
              <Input
                label="Password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              
              <Button 
                type="submit" 
                fullWidth 
                isLoading={isLoading}
              >
                Sign In
              </Button>
              
              <div className="mt-4 text-center">
                <p className="text-sm text-muted-foreground">
                  Don't have an account?{' '}
                  <Link to="/signup" className="text-primary font-medium hover:underline">
                    Sign up
                  </Link>
                </p>
              </div>
            </form>
            
            <div className="mt-8 pt-8 border-t border-border">
              <Button 
                variant="outline" 
                fullWidth 
                className="mb-3"
                isLoading={oauthLoading === 'google'}
                onClick={() => handleOAuthLogin('google')}
              >
                <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                  <path d="M1 1h22v22H1z" fill="none" />
                </svg>
                Continue with Google
              </Button>
              
              <Button 
                variant="outline" 
                fullWidth
                isLoading={oauthLoading === 'microsoft'}
                onClick={() => handleOAuthLogin('microsoft')}
              >
                <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M11.5 3.5v8h-8v-8h8zm8 0v8h-8v-8h8zm-8 8v8h-8v-8h8zm8 0v8h-8v-8h8z"/>
                </svg>
                Continue with Microsoft
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </LandingLayout>
  );
};

export default Login;
